import { create } from "zustand";

import { TIME_ZONE_AUTO, isKnownTimeZone, type TimeZonePref } from "./datetime.js";
import type { PushJobMode } from "@tg/shared";

// Локальные UI-предпочтения (браузер-специфичные, как тема): расположение
// папок и перевод удалённых. Живут в localStorage — не в IDB-persist стора,
// чтобы читались синхронно на первом render и не зависели от гидратации.

export type FoldersLayout = "top" | "left";

/**
 * Как показывать шильдики папок под строкой диалога:
 *   "compact" — 2 штуки + «+N» со списком остальных в подсказке (как в
 *     оригинальном Telegram, дефолт);
 *   "all" — все папки, в несколько строк (прежнее поведение).
 */
export type FolderChipsMode = "compact" | "all";

/** Сколько шильдиков видно в компактном режиме. */
export const FOLDER_CHIPS_COMPACT_LIMIT = 2;

const FOLDERS_LAYOUT_KEY = "tg-client:folders-layout";
const FOLDER_CHIPS_MODE_KEY = "tg-client:folder-chips-mode";
const COUNT_MUTED_IN_FOLDER_BADGES_KEY = "tg-client:count-muted-folder-badges";
const TRANSLATE_DELETED_KEY = "tg-client:translate-deleted";
const UI_SCALE_KEY = "tg-client:ui-scale";
const DIALOGS_PANE_WIDTH_KEY = "tg-client:dialogs-pane-width";
const CHAT_HEADER_COLLAPSED_KEY = "tg-client:chat-header-collapsed";
// Скрыть ряд кнопок «Поиск / Теги / Удалённые / …» над лентой (кнопка в
// шапке чата). Панели (поиск по Ctrl+F и т.п.) при этом продолжают работать.
const CHAT_TOOLS_HIDDEN_KEY = "tg-client:chat-tools-hidden";
// «Широкий чат»: лента/шапка/композер растягиваются на всю область чата
// вместо колонки 696px по центру (как в Telegram Desktop без «адаптивной
// ширины»). По умолчанию включено — так просил заказчик.
const CHAT_WIDE_KEY = "tg-client:chat-wide";
// Часовой пояс саппорта: все даты/время в UI (лента, список диалогов,
// отложенная отправка) рендерятся в нём. Per-браузер, как тема и масштаб —
// на одну учётку Telegram заходят разные люди из разных поясов.
const TIME_ZONE_KEY = "tg-client:time-zone";
// Правая рабочая панель (Инфо/Шаблоны/Отложенные/Удалённые/Способы оплаты):
// открыта/закрыта, активная вкладка и ширина (ресайз левой границей).
const RIGHT_PANEL_OPEN_KEY = "tg-client:right-panel-open";
const RIGHT_PANEL_TAB_KEY = "tg-client:right-panel-tab";
const RIGHT_PANEL_WIDTH_KEY = "tg-client:right-panel-width";
// Быстрые действия (⌘M): какие пункты показывать. Порядок фиксирован
// (QUICK_ACTION_CATALOG), пользователь только включает/выключает — номер
// пункта (1…9, 0) считается по видимым.
const QUICK_ACTIONS_KEY = "tg-client:quick-actions";

export type QuickActionId =
  | "translate"
  | "tag"
  | "move"
  | "template"
  | "payments"
  | "schedule"
  | "call"
  | "info"
  | "archive"
  | "pin"
  | "ai-folder-push";

export interface QuickActionMeta {
  id: QuickActionId;
  title: string;
  description: string;
}

/**
 * Каталог быстрых действий в порядке показа. Первые десять получают цифры
 * 1…9,0 в палитре; остальные показываются без номера — цифра для них не
 * работает, только клик/стрелки. Пункт «ИИ-пропуш» не требует выбранного
 * чата, действует на АКТИВНУЮ вкладку папок.
 */
export const QUICK_ACTION_CATALOG: QuickActionMeta[] = [
  { id: "translate", title: "Перевести весь чат", description: "Включить/выключить перевод входящих в открытом чате." },
  { id: "tag", title: "Поставить тег", description: "Внутренний тег чата (VIP, Депозит, Проблемный…)." },
  { id: "move", title: "Переместить пользователя в папку", description: "Добавить чат в папку Telegram или локальную папку." },
  { id: "template", title: "Отправить шаблон", description: "Список утверждённых шаблонов аккаунта — отправка одним нажатием." },
  { id: "payments", title: "Открыть способы оплаты", description: "Вкладка «Оплата» в правой панели." },
  { id: "schedule", title: "Запланировать сообщение", description: "Вкладка «Отложенные» в правой панели." },
  { id: "call", title: "Позвонить", description: "Пропуш-звонок пользователю (сбрасывается сразу)." },
  { id: "info", title: "Открыть Info пользователя", description: "Вкладка «Инфо» в правой панели." },
  { id: "archive", title: "Отправить пользователя в архив", description: "Убрать чат в архив (или вернуть из него)." },
  { id: "pin", title: "Закрепить пользователя в папке", description: "Закрепить/открепить чат в одной из его папок." },
  {
    id: "ai-folder-push",
    title: "ИИ-пропуш по папке",
    description:
      "Открыть модалку пропуша для АКТИВНОЙ вкладки папок (folder/local). Для «Общих»/архива покажет подсказку выбрать папку."
  }
];

const QUICK_ACTION_IDS = new Set<string>(QUICK_ACTION_CATALOG.map((action) => action.id));

const readQuickActionIds = (): QuickActionId[] => {
  const all = QUICK_ACTION_CATALOG.map((action) => action.id);
  if (typeof window === "undefined") return all;
  try {
    const raw = window.localStorage.getItem(QUICK_ACTIONS_KEY);
    if (!raw) return all;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return all;
    const enabled = new Set(
      parsed.filter((value): value is QuickActionId => typeof value === "string" && QUICK_ACTION_IDS.has(value))
    );
    // Порядок — каталожный, чтобы номера не «плавали» от порядка сохранения.
    return all.filter((id) => enabled.has(id));
  } catch {
    return all;
  }
};

export const UI_SCALE_MIN = 0.8;
export const UI_SCALE_MAX = 1.35;
export const UI_SCALE_DEFAULT = 1;

export const DIALOGS_PANE_WIDTH_MIN = 260;
export const DIALOGS_PANE_WIDTH_MAX = 640;
export const DIALOGS_PANE_WIDTH_DEFAULT = 360;

export const RIGHT_PANEL_WIDTH_MIN = 300;
export const RIGHT_PANEL_WIDTH_MAX = 520;
export const RIGHT_PANEL_WIDTH_DEFAULT = 360;

export type RightPanelTab = "info" | "templates" | "scheduled" | "deleted" | "payments";
const RIGHT_PANEL_TABS: RightPanelTab[] = [
  "info",
  "templates",
  "scheduled",
  "deleted",
  "payments"
];

const readRightPanelOpen = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(RIGHT_PANEL_OPEN_KEY) === "1";
  } catch {
    return false;
  }
};

const readRightPanelTab = (): RightPanelTab => {
  if (typeof window === "undefined") return "info";
  try {
    const raw = window.localStorage.getItem(RIGHT_PANEL_TAB_KEY);
    return raw && (RIGHT_PANEL_TABS as string[]).includes(raw)
      ? (raw as RightPanelTab)
      : "info";
  } catch {
    return "info";
  }
};

const readRightPanelWidth = (): number => {
  if (typeof window === "undefined") return RIGHT_PANEL_WIDTH_DEFAULT;
  try {
    const raw = window.localStorage.getItem(RIGHT_PANEL_WIDTH_KEY);
    if (!raw) return RIGHT_PANEL_WIDTH_DEFAULT;
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed)
      ? clampRightPanel(parsed)
      : RIGHT_PANEL_WIDTH_DEFAULT;
  } catch {
    return RIGHT_PANEL_WIDTH_DEFAULT;
  }
};

const clampRightPanel = (value: number): number =>
  Math.min(RIGHT_PANEL_WIDTH_MAX, Math.max(RIGHT_PANEL_WIDTH_MIN, value));

const readFoldersLayout = (): FoldersLayout => {
  if (typeof window === "undefined") {
    return "top";
  }
  try {
    return window.localStorage.getItem(FOLDERS_LAYOUT_KEY) === "left" ? "left" : "top";
  } catch {
    return "top";
  }
};

// По умолчанию удалённые переводятся (как и остальные сообщения чата) —
// опция существует, чтобы ЖЕЛАЮЩИЕ могли отключить и не тратить переводчик.
const readTranslateDeleted = (): boolean => {
  if (typeof window === "undefined") {
    return true;
  }
  try {
    return window.localStorage.getItem(TRANSLATE_DELETED_KEY) !== "0";
  } catch {
    return true;
  }
};

const readFolderChipsMode = (): FolderChipsMode => {
  if (typeof window === "undefined") {
    return "compact";
  }
  try {
    return window.localStorage.getItem(FOLDER_CHIPS_MODE_KEY) === "all" ? "all" : "compact";
  } catch {
    return "compact";
  }
};

// По умолчанию выключено — так бейджи папок совпадают с официальным
// Telegram. Включают те, у кого рабочие чаты сплошь без звука и счётчики
// нужны всё равно.
const readCountMutedInFolderBadges = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return window.localStorage.getItem(COUNT_MUTED_IN_FOLDER_BADGES_KEY) === "1";
  } catch {
    return false;
  }
};

const readTimeZone = (): TimeZonePref => {
  if (typeof window === "undefined") {
    return TIME_ZONE_AUTO;
  }
  try {
    const raw = window.localStorage.getItem(TIME_ZONE_KEY);
    return raw && isKnownTimeZone(raw) ? raw : TIME_ZONE_AUTO;
  } catch {
    return TIME_ZONE_AUTO;
  }
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

const readUiScale = (): number => {
  if (typeof window === "undefined") {
    return UI_SCALE_DEFAULT;
  }
  try {
    const raw = window.localStorage.getItem(UI_SCALE_KEY);
    if (!raw) return UI_SCALE_DEFAULT;
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) ? clamp(parsed, UI_SCALE_MIN, UI_SCALE_MAX) : UI_SCALE_DEFAULT;
  } catch {
    return UI_SCALE_DEFAULT;
  }
};

const readDialogsPaneWidth = (): number => {
  if (typeof window === "undefined") {
    return DIALOGS_PANE_WIDTH_DEFAULT;
  }
  try {
    const raw = window.localStorage.getItem(DIALOGS_PANE_WIDTH_KEY);
    if (!raw) return DIALOGS_PANE_WIDTH_DEFAULT;
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed)
      ? clamp(parsed, DIALOGS_PANE_WIDTH_MIN, DIALOGS_PANE_WIDTH_MAX)
      : DIALOGS_PANE_WIDTH_DEFAULT;
  } catch {
    return DIALOGS_PANE_WIDTH_DEFAULT;
  }
};

const readChatHeaderCollapsed = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return window.localStorage.getItem(CHAT_HEADER_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
};

const readChatToolsHidden = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return window.localStorage.getItem(CHAT_TOOLS_HIDDEN_KEY) === "1";
  } catch {
    return false;
  }
};

const readChatWide = (): boolean => {
  if (typeof window === "undefined") {
    return true;
  }
  try {
    return window.localStorage.getItem(CHAT_WIDE_KEY) !== "0";
  } catch {
    return true;
  }
};

const writeStorage = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // квота/приватный режим — предпочтение просто не переживёт reload
  }
};

// Применяем к :root сразу, чтобы стили съедали значение до первого render'а
// (иначе моргает при init).
// UI-scale: используем CSS `zoom` на <html>, а не font-size — код чата
// массово использует px для пузырей/аватарок/gap, и rem/em масштабирование
// на них не срабатывало. zoom реально пересчитывает layout как «увеличение
// страницы»; поддержан всеми современными браузерами.
// Множитель для viewport-единиц внутри zoom-контекста (--vh/--vh-static/--vw
// в styles.css). Chrome и Safari трактуют vh/dvh под `zoom` по-разному:
//   * Safari (старый WebKit-zoom): 100dvh уже равен «высота окна / zoom»,
//     то есть ровно экран — множитель 1, всё работает само;
//   * Chrome (zoom стандартизован с 128): vh/dvh НЕ учитывают zoom, поэтому
//     блок в 100dvh при масштабе 0.8 занимает 80% экрана (снизу пустота), а
//     при 1.35 — вылезает за нижний край. Нужен множитель 1/zoom.
// Не разбираем UA, а МЕРЯЕМ зондом: если браузер изменит поведение (или
// появится третий движок), значение подстроится само. Множитель не зависит от
// размера окна — только от семантики zoom, поэтому пересчитываем лишь при
// смене масштаба.
const applyViewportUnitFactor = (uiScale: number): void => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const measure = (): void => {
    if (!document.body) return;
    const probe = document.createElement("div");
    probe.style.cssText =
      "position:fixed;top:0;left:0;width:1px;height:100vh;visibility:hidden;pointer-events:none";
    document.body.appendChild(probe);
    // offsetHeight — логические px в обоих браузерах (в отличие от rect).
    const logical100vh = probe.offsetHeight;
    probe.remove();
    if (logical100vh <= 0) return;
    const targetLogical = window.innerHeight / uiScale;
    const factor = targetLogical / logical100vh;
    // Санити-кламп: любое неожиданное значение не должно ломать вёрстку —
    // 1 означает «как было», то есть поведение Safari.
    const safe = factor > 0.2 && factor < 5 ? factor : 1;
    root.style.setProperty("--vh-factor", safe.toFixed(4));
  };
  if (document.body) {
    measure();
    // Повтор в следующем кадре. Синхронного замера мало: сразу после записи
    // root.style.zoom Chrome может отдать зонду ещё СТАРУЮ геометрию, и тогда
    // --vh-factor остаётся от прошлого масштаба — вся полноэкранная вёрстка
    // (колонки, композер, модалки) уезжает до перезагрузки страницы.
    // В Safari второй замер даёт то же значение и ничего не меняет.
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => requestAnimationFrame(measure));
    }
  } else {
    document.addEventListener("DOMContentLoaded", measure, { once: true });
  }
};

// Смена размера окна (или выход из полноэкранного режима) тоже меняет
// соотношение visual/logical — перемеряем, иначе после ресайза остаётся
// фактор от прежней геометрии.
if (typeof window !== "undefined") {
  let resizeTimer: number | undefined;
  window.addEventListener("resize", () => {
    if (resizeTimer !== undefined) {
      window.clearTimeout(resizeTimer);
    }
    resizeTimer = window.setTimeout(() => {
      resizeTimer = undefined;
      applyViewportUnitFactor(useUiPrefs.getState().uiScale);
    }, 150);
  });
}

// Применяем к :root сразу, чтобы стили съедали значение до первого render'а
// (иначе моргает при init).
// UI-scale: используем CSS `zoom` на <html>, а не font-size — код чата
// массово использует px для пузырей/аватарок/gap, и rem/em масштабирование
// на них не срабатывало. zoom реально пересчитывает layout как «увеличение
// страницы»; поддержан всеми современными браузерами.
const applyDocumentPrefs = (uiScale: number, dialogsPaneWidth: number): void => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.style.setProperty("--ui-scale", String(uiScale));
  root.style.zoom = String(uiScale);
  root.style.setProperty("--dialogs-pane-width", `${dialogsPaneWidth}px`);
  applyViewportUnitFactor(uiScale);
};

interface UiPrefsState {
  foldersLayout: FoldersLayout;
  folderChipsMode: FolderChipsMode;
  /** Учитывать чаты со снятым звуком в бейджах папок (по умолчанию нет). */
  countMutedInFolderBadges: boolean;
  translateDeleted: boolean;
  uiScale: number;
  dialogsPaneWidth: number;
  chatHeaderCollapsed: boolean;
  chatToolsHidden: boolean;
  chatWide: boolean;
  /**
   * "auto" (пояс устройства) либо IANA-имя вроде "Europe/Moscow".
   * Значение прокидывается ЯВНО в форматтеры из lib/datetime.ts — компоненты,
   * которые рисуют даты, подписаны на это поле и перерисовываются при смене.
   */
  timeZone: TimeZonePref;
  /** Правая рабочая панель: открыта, активная вкладка, ширина. */
  rightPanelOpen: boolean;
  rightPanelTab: RightPanelTab;
  rightPanelWidth: number;
  setRightPanelOpen(open: boolean): void;
  toggleRightPanel(): void;
  setRightPanelTab(tab: RightPanelTab): void;
  setRightPanelWidth(width: number): void;
  /** Сигнал «открыть поиск по чату» из шапки (🔍). Поиск живёт в ChatView
   *  локально — счётчик служит мостом: инкремент → ChatView открывает панель. */
  chatSearchNonce: number;
  openChatSearch(): void;
  /** Сигнал «открыть управление шаблонами (админ-редактор)» из правой панели. */
  trustManageNonce: number;
  openTrustManage(): void;
  /**
   * Мост «открыть модалку ИИ-пропуша» из ⌘M/контекстного меню в DialogList.
   * Локальный useState в DialogList не годится: палитра — соседний компонент.
   * DialogList рендерит модалку, пока target !== null.
   */
  pushLaunchTarget: { listId: string; title: string; mode: PushJobMode } | null;
  /** mode по умолчанию — "ai"; "scripts" — пуш своими текстами (запрос №10). */
  openPushLaunch(listId: string, title: string, mode?: PushJobMode): void;
  closePushLaunch(): void;
  /** Включённые быстрые действия (⌘M) в каталожном порядке. */
  quickActionIds: QuickActionId[];
  setQuickActionEnabled(id: QuickActionId, enabled: boolean): void;
  setFoldersLayout(layout: FoldersLayout): void;
  setFolderChipsMode(mode: FolderChipsMode): void;
  setCountMutedInFolderBadges(enabled: boolean): void;
  setTranslateDeleted(enabled: boolean): void;
  setUiScale(scale: number): void;
  setDialogsPaneWidth(width: number): void;
  setChatHeaderCollapsed(collapsed: boolean): void;
  setChatToolsHidden(hidden: boolean): void;
  setChatWide(wide: boolean): void;
  setTimeZone(timeZone: TimeZonePref): void;
}

const initialUiScale = readUiScale();
const initialDialogsPaneWidth = readDialogsPaneWidth();
const initialChatHeaderCollapsed = readChatHeaderCollapsed();
const initialChatToolsHidden = readChatToolsHidden();
const initialChatWide = readChatWide();
applyDocumentPrefs(initialUiScale, initialDialogsPaneWidth);
if (typeof document !== "undefined") {
  document.documentElement.dataset.chatHeaderCollapsed = initialChatHeaderCollapsed
    ? "1"
    : "0";
  document.documentElement.dataset.chatToolsHidden = initialChatToolsHidden ? "1" : "0";
  document.documentElement.dataset.chatWide = initialChatWide ? "1" : "0";
}

export const useUiPrefs = create<UiPrefsState>()((set) => ({
  foldersLayout: readFoldersLayout(),
  folderChipsMode: readFolderChipsMode(),
  countMutedInFolderBadges: readCountMutedInFolderBadges(),
  translateDeleted: readTranslateDeleted(),
  uiScale: initialUiScale,
  dialogsPaneWidth: initialDialogsPaneWidth,
  chatHeaderCollapsed: initialChatHeaderCollapsed,
  chatToolsHidden: initialChatToolsHidden,
  chatWide: initialChatWide,
  timeZone: readTimeZone(),
  rightPanelOpen: readRightPanelOpen(),
  rightPanelTab: readRightPanelTab(),
  rightPanelWidth: readRightPanelWidth(),
  setRightPanelOpen: (open) => {
    writeStorage(RIGHT_PANEL_OPEN_KEY, open ? "1" : "0");
    set({ rightPanelOpen: open });
  },
  toggleRightPanel: () => {
    set((state) => {
      const next = !state.rightPanelOpen;
      writeStorage(RIGHT_PANEL_OPEN_KEY, next ? "1" : "0");
      return { rightPanelOpen: next };
    });
  },
  setRightPanelTab: (tab) => {
    writeStorage(RIGHT_PANEL_TAB_KEY, tab);
    // Выбор вкладки всегда раскрывает панель, если была закрыта.
    writeStorage(RIGHT_PANEL_OPEN_KEY, "1");
    set({ rightPanelTab: tab, rightPanelOpen: true });
  },
  setRightPanelWidth: (width) => {
    const clamped = clampRightPanel(width);
    writeStorage(RIGHT_PANEL_WIDTH_KEY, String(clamped));
    set({ rightPanelWidth: clamped });
  },
  chatSearchNonce: 0,
  openChatSearch: () => set((state) => ({ chatSearchNonce: state.chatSearchNonce + 1 })),
  trustManageNonce: 0,
  openTrustManage: () => set((state) => ({ trustManageNonce: state.trustManageNonce + 1 })),
  pushLaunchTarget: null,
  openPushLaunch: (listId, title, mode = "ai") => set({ pushLaunchTarget: { listId, title, mode } }),
  closePushLaunch: () => set({ pushLaunchTarget: null }),
  quickActionIds: readQuickActionIds(),
  setQuickActionEnabled: (id, enabled) => {
    set((state) => {
      const current = new Set(state.quickActionIds);
      if (enabled) {
        current.add(id);
      } else {
        current.delete(id);
      }
      const next = QUICK_ACTION_CATALOG.map((action) => action.id).filter((actionId) =>
        current.has(actionId)
      );
      writeStorage(QUICK_ACTIONS_KEY, JSON.stringify(next));
      return { quickActionIds: next };
    });
  },
  setFoldersLayout: (layout) => {
    writeStorage(FOLDERS_LAYOUT_KEY, layout);
    set({ foldersLayout: layout });
  },
  setFolderChipsMode: (mode) => {
    writeStorage(FOLDER_CHIPS_MODE_KEY, mode);
    set({ folderChipsMode: mode });
  },
  setCountMutedInFolderBadges: (enabled) => {
    writeStorage(COUNT_MUTED_IN_FOLDER_BADGES_KEY, enabled ? "1" : "0");
    set({ countMutedInFolderBadges: enabled });
  },
  setTranslateDeleted: (enabled) => {
    writeStorage(TRANSLATE_DELETED_KEY, enabled ? "1" : "0");
    set({ translateDeleted: enabled });
  },
  setUiScale: (scale) => {
    const clamped = clamp(scale, UI_SCALE_MIN, UI_SCALE_MAX);
    writeStorage(UI_SCALE_KEY, String(clamped));
    if (typeof document !== "undefined") {
      document.documentElement.style.zoom = String(clamped);
      document.documentElement.style.setProperty("--ui-scale", String(clamped));
      applyViewportUnitFactor(clamped);
    }
    set({ uiScale: clamped });
  },
  setDialogsPaneWidth: (width) => {
    const clamped = clamp(width, DIALOGS_PANE_WIDTH_MIN, DIALOGS_PANE_WIDTH_MAX);
    writeStorage(DIALOGS_PANE_WIDTH_KEY, String(clamped));
    if (typeof document !== "undefined") {
      document.documentElement.style.setProperty("--dialogs-pane-width", `${clamped}px`);
    }
    set({ dialogsPaneWidth: clamped });
  },
  setChatHeaderCollapsed: (collapsed) => {
    writeStorage(CHAT_HEADER_COLLAPSED_KEY, collapsed ? "1" : "0");
    // Ставим data-атрибут на <html>, чтобы CSS мог прятать не только шапку,
    // но и панель .chat-tools (кнопки Поиск/Теги/Шаблоны/Переводы),
    // которая живёт в ChatView, а не внутри .pane-header--chat.
    if (typeof document !== "undefined") {
      document.documentElement.dataset.chatHeaderCollapsed = collapsed ? "1" : "0";
    }
    set({ chatHeaderCollapsed: collapsed });
  },
  setChatToolsHidden: (hidden) => {
    writeStorage(CHAT_TOOLS_HIDDEN_KEY, hidden ? "1" : "0");
    if (typeof document !== "undefined") {
      document.documentElement.dataset.chatToolsHidden = hidden ? "1" : "0";
    }
    set({ chatToolsHidden: hidden });
  },
  setChatWide: (wide) => {
    writeStorage(CHAT_WIDE_KEY, wide ? "1" : "0");
    if (typeof document !== "undefined") {
      document.documentElement.dataset.chatWide = wide ? "1" : "0";
    }
    set({ chatWide: wide });
  },
  setTimeZone: (timeZone) => {
    const next = isKnownTimeZone(timeZone) ? timeZone : TIME_ZONE_AUTO;
    writeStorage(TIME_ZONE_KEY, next);
    set({ timeZone: next });
  }
}));
